import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Package, Truck, CheckCircle2, Clock } from "lucide-react";

const STATUS_LABEL = {
  pending: { label: "En attente", icon: Clock, color: "text-brand-gray" },
  paid: { label: "Payée", icon: CheckCircle2, color: "text-brand-green" },
  shipped: { label: "Expédiée", icon: Truck, color: "text-brand-lightBlue" },
  delivered: { label: "Livrée", icon: CheckCircle2, color: "text-brand-green" },
  cancelled: { label: "Annulée", icon: Clock, color: "text-red-400" },
};

export default function AccountPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/orders/mine");
        setOrders(data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div data-testid="account-page">
      <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-green mb-2">Mon compte</div>
      <h1 className="text-4xl font-black tracking-tighter text-white">Bonjour {user?.name}</h1>
      <p className="mt-2 text-sm text-brand-gray font-roboto">{user?.email}</p>

      <div className="mt-12">
        <h2 className="text-2xl font-extrabold text-white tracking-tight mb-5">Mes commandes</h2>
        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-brand-green" /></div>
        ) : orders.length === 0 ? (
          <div className="border border-brand-blue/40 rounded-sm p-10 text-center text-brand-gray font-roboto" data-testid="orders-empty">
            <Package className="h-10 w-10 mx-auto text-brand-green mb-4" />
            Aucune commande pour l'instant.
          </div>
        ) : (
          <div className="space-y-4" data-testid="orders-list">
            {orders.map((o) => {
              const meta = STATUS_LABEL[o.status] || STATUS_LABEL.pending;
              const Icon = meta.icon;
              return (
                <div key={o.id} className="border border-brand-blue/40 rounded-sm p-5" data-testid={`order-${o.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.25em] text-brand-gray/70 font-bold">
                        Commande #{o.id.slice(0, 8)}
                      </div>
                      <div className="text-xs text-brand-gray/60 font-roboto mt-1">
                        {new Date(o.created_at).toLocaleString("fr-FR")}
                      </div>
                    </div>
                    <div className={`inline-flex items-center gap-2 text-sm font-bold ${meta.color}`}>
                      <Icon className="h-4 w-4" /> {meta.label}
                    </div>
                    <div className="font-black text-xl text-white tracking-tight">
                      {o.total.toFixed(2)} €
                    </div>
                  </div>
                  <ul className="mt-4 space-y-1 text-sm text-brand-gray font-roboto">
                    {o.items.map((it) => (
                      <li key={it.product_id} className="flex justify-between">
                        <span className="truncate">{it.name} × {it.quantity}</span>
                        <span>{it.line_total.toFixed(2)} €</span>
                      </li>
                    ))}
                  </ul>
                  {o.tracking_number && (
                    <div className="mt-3 text-xs text-brand-gray font-roboto">
                      Suivi : <span className="text-white font-bold">{o.tracking_number}</span>
                      {o.carrier && <> · {o.carrier}</>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
